import * as React from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Calendar } from '@/components/ui/Calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';

export interface DatePickerProps {
  value?: Date | string;
  onChange: (date: Date | undefined) => void;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  error?: string;
  className?: string;
}

export function DatePicker({
  value,
  onChange,
  placeholder = 'Select date',
  minDate,
  maxDate,
  disabled = false,
  error,
  className,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);

  // Convert string date to Date object
  const dateValue = value
    ? typeof value === 'string'
      ? new Date(value)
      : value
    : undefined;

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onChange(undefined);
  };

  return (
    <div className={cn('relative', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            data-empty={!dateValue}
            className={cn(
              'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background pl-3 py-2 text-sm',
              'focus:outline-2 focus:outline-offset-2 focus:outline-blue-500',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'data-[empty=true]:text-muted-foreground',
              error && 'border-destructive'
            )}
          >
            <span className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4 text-muted-foreground" />
              {dateValue ? format(dateValue, 'PPP') : placeholder}
            </span>
            {dateValue && (
              <button
                type="button"
                onClick={handleClear}
                className="hover:bg-muted rounded-md p-1 focus:outline-2 focus:outline-offset-2 focus:outline-blue-500 flex items-center justify-center"
                aria-label="Clear date"
              >
                <X className="h-6 w-6 text-muted-foreground" />
              </button>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={dateValue}
            captionLayout="dropdown"
            startMonth={minDate || new Date(1900, 0)}
            endMonth={maxDate || new Date(2100, 11)}
            disabled={[
              ...(minDate ? [{ before: minDate }] : []),
              ...(maxDate ? [{ after: maxDate }] : []),
            ]}
            onSelect={(date) => {
              onChange(date);
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>

      {error && (
        <p className="mt-2 text-sm text-destructive">{error}</p>
      )}
    </div>
  );
}
